import React from 'react';

import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { AlineTooltip } from 'src/components';
import type { ReminderListRow } from 'src/components/reminders/ColumnDef';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  badge: {
    alignItems: 'center',
    backgroundColor: theme.appColors.primary,
    borderRadius: 12,
    color: theme.palette.common.white,
    cursor: 'pointer',
    display: 'flex',
    fontSize: 12,
    fontWeight: 600,
    height: 22,
    justifyContent: 'center',
    minWidth: 22,
    padding: theme.spacing(0.25, 0.75),
  },
  cell: {
    alignItems: 'center',
    display: 'flex',
    width: '100%',
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    textAlign: 'left',
  },
}));

function formatOffsetDisplay(
  offset: Pick<Apollo.NotificationOffset, 'amount' | 'position' | 'unit'>
): string {
  const { amount, position, unit } = offset;
  const unitLower = unit.toLowerCase();
  const unitLabel = amount === 1 ? unitLower.replace(/s$/, '') : unitLower;
  return `${amount} ${unitLabel} ${position.toLowerCase()}`;
}

export function ReminderRemindMeCell({
  row,
}: GridCellParams<ReminderListRow, ReminderListRow['notificationOffsets']>) {
  const { classes } = useStyles();
  const offsets = row.notificationOffsets ?? [];

  if (!offsets.length) return <Typography color="textSecondary">—</Typography>;

  const tooltipContent = (
    <div className={classes.tooltipContent}>
      <Typography
        sx={theme => ({ fontSize: 12, fontWeight: 600, marginBottom: theme.spacing(0.5) })}
        variant="body2"
      >
        Notifications
      </Typography>
      {offsets.map((offset, idx) => (
        <Typography key={idx} sx={{ fontSize: 12 }} variant="body2">
          {formatOffsetDisplay(offset)}
        </Typography>
      ))}
    </div>
  );

  return (
    <AlineTooltip arrow placement="top" title={tooltipContent}>
      <Typography>{`${offsets.length} notification${offsets.length > 1 ? 's' : ''}`}</Typography>
    </AlineTooltip>
  );
}
